import { Button } from '@radix-ui/themes';
import React from 'react';
import { Flex, Spinner, TextField, TextArea } from '@radix-ui/themes';
import { api } from '../../services/api';
import { useStore } from '../../store/store';
import Icon from '@mdi/react';
import { mdiCreation } from '@mdi/js';
import { Message } from 'primereact/message';
import avatar from '../../assets/avatar4.png';


export const CharacterCreation = ({ characters, setCharacters, novelId}) => {
    const [characterName, setCharacterName] = React.useState('');
    const [characterRole, setCharacterRole] = React.useState('');
    const [characterAge, setCharacterAge] = React.useState('');
    const [characterPhys, setCharacterPhys] = React.useState('');
    const [characterPsych, setCharacterPsych] = React.useState('');
    const [characterCloth, setCharacterCloth] = React.useState('');
    const [characterInnerMono, setCharacterInnerMono] = React.useState('');
    const [characterManners, setCharacterManners] = React.useState('');
    const [characterMotivation, setCharacterMotivation] = React.useState('');
    const [characterFears, setCharacterFears] = React.useState('');
    const [characterMisc, setCharacterMisc] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [editMode, setEditMode] = React.useState(false);
    const [editIndex, setEditIndex] = React.useState(-1);
    const [state, actions] = useStore();

    const resetForm = () => {
        setCharacterName('');
        setCharacterRole('');
        setCharacterAge('');
        setCharacterPhys('');
        setCharacterPsych('');
        setCharacterCloth('');
        setCharacterInnerMono('');
        setCharacterManners('');
        setCharacterMotivation('');
        setCharacterFears('');
        setCharacterMisc('');
    };

    const addCharacter = () => {
        setCharacters([...characters, {
            name: characterName, 
            role: characterRole,
            age: characterAge,
            physicalAppearance: characterPhys,
            psychologicalProfile: characterPsych,
            clothingStyle: characterCloth,
            innerMonologueStyle: characterInnerMono,
            communicationMannerisms: characterManners,
            motivations: characterMotivation,
            fears: characterFears,
            miscellaneous: characterMisc
        }]);
        resetForm();
    }

    const updateCharacter = () => {
        const updatedCharacters = [...characters];
        updatedCharacters[editIndex] = {
            name: characterName, 
            role: characterRole,
            age: characterAge,
            physicalAppearance: characterPhys,
            psychologicalProfile: characterPsych,
            clothingStyle: characterCloth,
            innerMonologueStyle: characterInnerMono,
            communicationMannerisms: characterManners,
            motivations: characterMotivation,
            fears: characterFears,
            miscellaneous: characterMisc
        };
        setCharacters(updatedCharacters);
        resetForm();
        setEditMode(false);
        setEditIndex(-1);
    }

    const dismissChanges = () => {
        resetForm();
        setEditMode(false);
        setEditIndex(-1);
    }

    const loadCharacterData = (index) => {
        const character = characters[index];
        setCharacterName(character.name);
        setCharacterRole(character.role);
        setCharacterAge(character.age);
        setCharacterPhys(character.physicalAppearance);
        setCharacterPsych(character.psychologicalProfile);
        setCharacterCloth(character.clothingStyle);
        setCharacterInnerMono(character.innerMonologueStyle);
        setCharacterManners(character.communicationMannerisms);
        setCharacterMotivation(character.motivations);
        setCharacterFears(character.fears);
        setCharacterMisc(character.miscellaneous);
        setEditMode(true);
        setEditIndex(index);
    }

    const generateCharacter = async () => {
        setIsLoading(true);
        try {
            const result = await api.generateCharacters(novelId, characters);
            const generatedCharacter = result.character[0];

            setCharacterName(generatedCharacter.name);
            setCharacterRole(generatedCharacter.role);
            setCharacterAge(generatedCharacter.age);
            setCharacterPhys(generatedCharacter.physical_attributes);
            setCharacterPsych(generatedCharacter.psychological_profile);
            setCharacterCloth(generatedCharacter.clothing_style);
            setCharacterInnerMono(generatedCharacter.inner_monologue_tone);
            setCharacterManners(generatedCharacter.communication_mannerisms);
            setCharacterMotivation(generatedCharacter.motivation);
            setCharacterFears(generatedCharacter.fears);
            setCharacterMisc(generatedCharacter.miscellaneous);

        } catch (error) {
            console.error("Error generating character:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const deleteCharacter = (index) => {
        const updatedCharacters = [...characters];
        updatedCharacters.splice(index, 1);
        setCharacters(updatedCharacters);
        
        // If we're editing the character that gets deleted, reset the form
        if (editMode && editIndex === index) {
            dismissChanges();
        } else if (editMode && editIndex > index) {
            // If we're editing a character that comes after the deleted one, adjust the index
            setEditIndex(editIndex - 1);
        }
    }

    const charactersMarkup = characters.map((character, index) => {
        return (
            <div 
                key={index} 
                className={'character-create-card'}
                onClick={() => loadCharacterData(index)}
                style={{ cursor: 'pointer', border: editMode && editIndex === index ? '2px solid #6221ab' : 'none' }}
            >
                <div className={'character-create-card-name'}>{character.name} ({character.age})</div>
                <div className={'character-create-card-role'}>{character.role}</div>
                <button 
                    className="character-create-card-delete" 
                    onClick={(e) => {
                        e.stopPropagation();
                        deleteCharacter(index);
                    }}
                >
                    &times;
                </button>
            </div>
        );
    });

    return (
        <div>
            <h1 style={{fontFamily: 'Poppins'}}>Characters</h1>

            {state.novels.length === 0 ?
                <Flex className="tutorial-container" style={{}}>
                    <Flex className={"tutorial-text"}>
                        <img src={avatar} alt="Avatar" className="tutorial-avatar" />
                        <div>
                            Here we can add a few characters to the story. You can either enter the details yourself or use AI to generate them. When you're happy with the results, hit the 'Add Character' button and the character will appear on the top.<br />
                            <br />This step is only needed if you plan to generate the entire novel outline using AI (in the last step). If you want to add the novels chapters and scenes yourself (as your novel grows), you can skip this step and add characters later.<br />
                            <br />
                        </div>
                    </Flex>
                </Flex> : null}

            <Flex gap="20px">
                {charactersMarkup}
            </Flex>
            <br />
            <Flex direction="column" gap="10px">
                <Flex direction="row" gap="10px">
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Character Name</div>
                        <TextField.Root
                            placeholder="Name"
                            value={characterName}
                            onChange={(e) => {setCharacterName(e.target.value)}}
                            variant='soft'
                            className={'create-textfield'}
                        />
                    </div>
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Character Role</div>
                        <TextField.Root
                            placeholder="Role"
                            value={characterRole}
                            onChange={(e) => {setCharacterRole(e.target.value)}}
                            variant='soft'
                            className={'create-textfield'}
                        />
                    </div>
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Character Age</div>
                        <TextField.Root
                        placeholder="Age"
                        value={characterAge}
                        onChange={(e) => {setCharacterAge(e.target.value)}}
                        variant='soft'
                        className={'create-textfield'}
                    />
                    </div>
                    
                </Flex>

                <Flex gap="30px">
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Physical Appearance</div>
                        <TextArea className={'create-textarea character-textarea'} value={characterPhys} variant="soft" placeholder="Physical Appearance" onChange={(e) => {setCharacterPhys(e.target.value)}} />
                    </div>
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Psycological Profile</div>
                        <TextArea className={'create-textarea character-textarea'} value={characterPsych} variant="soft" placeholder="Psycological Profile" onChange={(e) => {setCharacterPsych(e.target.value)}} />
                    </div>
                </Flex>
                <Flex gap="30px">
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Clothing Style</div>
                        <TextArea className={'create-textarea character-textarea'} value={characterCloth} variant="soft" placeholder="Clothing Style" onChange={(e) => {setCharacterCloth(e.target.value)}} />
                        </div>
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Inner Monologue Style</div>
                        <TextArea className={'create-textarea character-textarea'} value={characterInnerMono} variant="soft" placeholder="Inner Monologue Style" onChange={(e) => {setCharacterInnerMono(e.target.value)}} />
                        </div>
                </Flex>
                <Flex gap="30px">
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Communication Mannerism</div>
                        <TextArea className={'create-textarea character-textarea'} value={characterManners} variant="soft" placeholder="Communication Mannerisms" onChange={(e) => {setCharacterManners(e.target.value)}} />
                    </div>
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Motivation</div>
                        <TextArea className={'create-textarea character-textarea'} value={characterMotivation} variant="soft" placeholder="Motivation" onChange={(e) => {setCharacterMotivation(e.target.value)}} />
                    </div>
                </Flex>
                <Flex gap="30px">
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Fears</div>
                        <TextArea className={'create-textarea character-textarea'} value={characterFears} variant="soft" placeholder="Fears" onChange={(e) => {setCharacterFears(e.target.value)}} />
                    </div>
                    <div className={'character-textarea-wrapper'}>
                        <div className='details-label'>Miscellaneous</div>
                        <TextArea className={'create-textarea character-textarea'} value={characterMisc} variant="soft" placeholder="Miscellaneous" onChange={(e) => {setCharacterMisc(e.target.value)}} />
                    </div>
                </Flex>
            </Flex>
            <br />
            <Flex gap="20px">
            {!editMode &&
                <Button 
                    onClick={() => {generateCharacter()}}
                    disabled={isLoading}
                    style={{background: '#6221ab', cursor: 'pointer'}}
                >
                    {isLoading ? (
                        <Flex gap="2" align="center">
                            <Spinner size="small" />
                            <span>Generating...</span>
                        </Flex>
                    ) : (
                        <>
                            <Icon style={{transform: 'scale(0.8)'}} path={mdiCreation} size={1} />
                            {`Generate ${characters.length > 1 && !editMode ? 'another' : ''} Character`}
                        </>
                    )}
                </Button>
            }
                <Button onClick={editMode ? updateCharacter : addCharacter} style={{cursor: 'pointer', background: 'hsl(227deg 40.1% 38.09%)'}}>
                    {editMode ? 'Update Character' : 'Add Character'}
                </Button>
                {editMode && (
                    <Button onClick={dismissChanges} style={{cursor: 'pointer', background: '#ff000088'}}>
                        Dismiss Changes
                    </Button>
                )}
            </Flex>
            <br />
            {characters.length < 2 && <Message severity="warn" text="It is recommended to have at least 2 characters before proceeding. You can add a new character by either entering the details yourself or using AI. When your happy with the results, hit the 'Add Character' button." />}
            <br />
            <br />
        </div>
    );
};

export default CharacterCreation;